import { Color, Size, WHATS_NEXT_STEP_LABEL, t, whatsNext, type StringKey, type WhatsNextStep } from '@bms2/shared';
import { Button } from '../controls';
import { Icon } from '../Icon';
import styles from './WhatsNextBanner.module.css';

export interface WhatsNextBannerProps {
  /** The document kind, which with its status picks the rule (decision 1732). */
  documentType: string;
  /** The stored status the rule is keyed on. */
  status: string | null | undefined;
  /**
   * The steps the page's own footer offers at this moment, each opening the
   * window that footer button opens. 🔨 A step left out is never drawn, so the
   * banner never offers what the server would refuse.
   */
  actions: Partial<Record<WhatsNextStep, () => void>>;
  disabled?: boolean;
  /**
   * 🔒 The next step a page read off its document's state rather than its
   * status alone, the Sales Order's (decision 1931). It replaces the table's
   * rule, and its sentence draws even where nothing is left to press.
   */
  next?: { message: StringKey; steps: readonly WhatsNextStep[] } | { text: string; steps: readonly WhatsNextStep[] } | null;
  /**
   * 🔒 The banner's tone (decision 1935): Info everywhere, and Warning on the
   * Deal alone, where an Urgent next action draws it (decision 1485).
   */
  tone?: 'info' | 'warning';
  /**
   * 🔒 The quiet close (decision 1935), the Deal's per-person hide of decision
   * 1218. Absent draws none, which is every document.
   */
  onDismiss?: () => void;
}

/**
 * 🔒 The What's next banner (decision 1732), the owner's *"whats next can be on
 * all documents"*: one control, drawn under the document's toolbar, naming the
 * step the status allows next and offering it as a button or two. The sentence
 * and the steps come from the shared rule table; the first step is the primary.
 *
 * 🔨 It draws nothing where the status has no rule, or where the page offers
 * none of the rule's steps.
 */
export function WhatsNextBanner({ documentType, status, actions, disabled = false, next: read, tone = 'info', onDismiss }: WhatsNextBannerProps) {
  const offered = (Object.keys(actions) as WhatsNextStep[]).filter((step) => typeof actions[step] === 'function');
  const ruled = read ? null : whatsNext(documentType, status, offered);
  const next = read
    ? { sentence: 'text' in read ? read.text : t(read.message), steps: read.steps.filter((step) => offered.includes(step)) }
    : ruled
      ? { sentence: t(ruled.message), steps: ruled.steps }
      : null;
  if (!next) return null;
  return (
    <section className={`${styles.banner} ${tone === 'warning' ? styles.warning : ''}`} aria-label={t('whatsNext.heading')} data-whats-next={documentType} data-whats-next-tone={tone} data-whats-next-steps={next.steps.join(' ')}>
      <div className={styles.words}>
        <h2 className={styles.heading}>{t('whatsNext.heading')}</h2>
        <p className={styles.message} data-whats-next-message="true">{next.sentence}</p>
      </div>
      <div className={styles.steps}>
        {next.steps.map((step, index) => (
          <span key={step} data-whats-next-step={step}>
            <Button size={Size.Small} {...(index === 0 ? { color: Color.Navy } : {})} text={[t(WHATS_NEXT_STEP_LABEL[step])]} actions={[actions[step]!]} disabled={disabled} />
          </span>
        ))}
      </div>
      {onDismiss ? (
        <button type="button" className={styles.dismiss} aria-label={t('banner.dismiss')} onClick={onDismiss} data-banner-dismiss="true">
          <Icon meaning="remove" size="header" />
        </button>
      ) : null}
    </section>
  );
}
